<script setup lang="ts">
/**
 * Root component.
 *
 * **The view key.** Keying on `fullPath` remounts the whole view whenever any
 * query parameter changes — so typing in a filter box tore down and rebuilt the
 * page, losing input focus and scroll position mid-keystroke. Keying on route
 * name + path params instead means a filter change updates in place, and only a
 * genuine navigation remounts.
 *
 * **There is no page transition.** Navigation swaps the view outright. The
 * wrapping `div` stays anyway: it is what carries the key, and keeping one
 * element here also keeps the door shut on the bug a transition once caused —
 * `<Transition>` takes exactly one *element* child, almost every view in this
 * app has a multi-root template, and a fragment never reports its leave as
 * finished. With `mode="out-in"`, which waits for that report, the first
 * client-side navigation on a signed-in session emptied the content area and
 * nothing came back. Nothing was logged and no request failed, which is how it
 * survived: it read as an app with nothing to show.
 */
import { computed } from 'vue';
import { useRoute, type RouteLocationNormalizedLoaded } from 'vue-router';

import CallOverlay from './components/CallOverlay.vue';
import LanguageSuggestion from './components/LanguageSuggestion.vue';
import MainLayout from './layouts/MainLayout.vue';
import AuthLayout from './layouts/AuthLayout.vue';
import DesktopLayout from './layouts/DesktopLayout.vue';
import LandingLayout from './layouts/LandingLayout.vue';
import { isDesktopShell } from './utils/desktop-shell.ts';
import { useAuthStore } from './stores/auth.ts';

const route = useRoute();
const auth = useAuthStore();

/** Read once: the user agent does not change while the page is open. */
const desktop = isDesktopShell();

const layout = computed(() => {
  if (route.meta.layout === 'auth') return AuthLayout;
  /**
   * The desktop app gets its own frame: the chat filling the window, with none
   * of the website's measure, navigation or brand mark. Signed-in only — signing
   * in is the same pair of pages everywhere, and they have their own layout.
   */
  if (desktop && auth.ready && auth.isAuthenticated) return DesktopLayout;
  if (auth.ready && !auth.isAuthenticated) {
    // Home is a dashboard for a member and a front door for a visitor, and
    // those want different chrome entirely - see `guestLayout`.
    if (route.meta.guestLayout === 'landing') return LandingLayout;
    if (route.meta.guestLayout === 'auth') return AuthLayout;
  }
  return MainLayout;
});

/**
 * Identity of the *page*, ignoring query parameters.
 *
 * `/@ploy` and `/@rin` are different pages and should remount; `/discover` and
 * `/discover?interest=music` are the same page in a different state.
 *
 * A route can say its path parameters do not change the page either, with
 * `meta.keepAcrossParams` — the inbox does, because `/messages/conv_1` is the
 * same inbox as `/messages` with one conversation open. Keying it on the
 * conversation id tore the list down and rebuilt it every time one was opened
 * or closed, which lost the filter, the search box and the scroll position: on
 * a phone, where the conversation covers the list and closing it is meant to
 * show what you left, you came back to the top of a freshly built list.
 */
function viewKey(route: RouteLocationNormalizedLoaded): string {
  const base = typeof route.name === 'string' ? route.name : 'view';
  if (route.meta.keepAcrossParams) return base;
  const values = Object.values(route.params)
    .map((v) => (Array.isArray(v) ? v.join('/') : String(v ?? '')))
    .filter((v) => v.length > 0);
  return values.length > 0 ? `${base}:${values.join(':')}` : base;
}
</script>

<template>
  <component :is="layout">
    <router-view v-slot="{ Component, route: current }">
      <!--
        One element, carrying the key; see above.

        It also has to carry the height in the desktop app, and that is easy to
        miss: a page there is meant to fill the window, and `height: 100%` on
        the page resolves against *this* div. Without a height of its own the
        chain breaks here — the chat became as tall as its messages instead of
        as tall as the window, and the composer sat wherever the transcript
        happened to end rather than against the bottom edge. On the website the
        question never arises: pages there size themselves from the viewport.
      -->
      <div :key="viewKey(current)" :class="desktop ? 'h-full' : ''">
        <component :is="Component" />
      </div>
    </router-view>
  </component>

  <!--
    Outside the layout, and `fixed` within itself, so it cannot move the page
    it appears over. It decides for itself whether to render at all, which is
    almost never.
  -->
  <LanguageSuggestion />

  <!--
    The call panel, mounted here rather than in the chat.
    A call starts in a conversation and must survive leaving it: the caller
    walks to the other person's profile mid-call, or answers from the inbox and
    keeps browsing. Mounted inside a view, it would be torn down by that
    navigation - and tearing it down closes the connection and drops the call.
  -->
  <CallOverlay />
</template>
